import { lazy, Suspense } from "react";
import useIsCaseManagementEnabled from "src/features/student-cases/hooks/useIsCaseManagementEnabled";
import AppSkeleton from "src/pages/AppSkeleton";

// Lazy so the Twilio Voice SDK (pulled in transitively by VoiceCallProvider)
// stays out of the main bundle until an admin session needs it.
const GlobalCallSurface = lazy(() => import("./GlobalCallSurface"));

/**
 * Gate + lazy boundary for the global in-app phone, mounted around the admin
 * Outlet. For case-management ("Dial") coaches it loads GlobalCallSurface, which
 * registers the Twilio Device on every admin page — the prerequisite for
 * receiving inbound calls anywhere, not just on the Dial screen. Non-admin
 * sessions get the children untouched: no provider, and the SDK chunk is never
 * fetched.
 *
 * The Suspense fallback is the app skeleton (not the bare children): a page like
 * the Dial screen calls useVoiceCall and would throw if it rendered before the
 * provider mounted, so we hold the shell until the surface chunk resolves.
 */
export default function GlobalCallLayer({
  children,
}: {
  children: JSX.Element;
}): JSX.Element {
  const { isEnabled } = useIsCaseManagementEnabled();

  // Admin status is known from the authenticated session, so every admin mounts
  // under the call provider without waiting for rollout configuration.
  if (!isEnabled) return children;

  return (
    <Suspense fallback={<AppSkeleton />}>
      <GlobalCallSurface>{children}</GlobalCallSurface>
    </Suspense>
  );
}
